import React, { useState, useEffect } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  Table,
  Badge,
  Button,
  ButtonGroup,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Input,
  Progress,
  Alert
} from 'reactstrap';
import { FiRefreshCw, FiFilter, FiDownload, FiPrinter, FiEye } from 'react-icons/fi';

const DashboardEncaissements = () => {
  // État pour les données et filtres
  const [encaissements, setEncaissements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('Tous');
  const [dateRange, setDateRange] = useState({
    start: new Date(new Date().setMonth(new Date().getMonth() - 1)),
    end: new Date()
  });
  const [dropdownOpen, setDropdownOpen] = useState(false);

  // Données simulées
  useEffect(() => {
    const mockData = [
      {
        id: 1,
        numero: 'CHQ-2023-001',
        client: 'Société ABC',
        montant: 12500,
        dateEmission: '2023-05-10',
        dateEcheance: '2023-06-10',
        statut: 'Encaissé',
        banque: 'CIH',
        compte: '123-456-789'
      },
      {
        id: 2,
        numero: 'CHQ-2023-002',
        client: 'Entreprise XYZ',
        montant: 8500,
        dateEmission: '2023-05-15',
        dateEcheance: '2023-06-15',
        statut: 'En attente',
        banque: 'BMCE',
        compte: '987-654-321'
      },
      {
        id: 3,
        numero: 'CHQ-2023-003',
        client: 'SARL DEF',
        montant: 22000,
        dateEmission: '2023-05-20',
        dateEcheance: '2023-06-20',
        statut: 'Rejeté',
        banque: 'Attijariwafa',
        compte: '456-789-123'
      }
    ];
    
    setTimeout(() => {
      setEncaissements(mockData);
      setLoading(false);
    }, 1000);
  }, []);

  // Filtrage des données
  const filteredData = encaissements.filter(item => {
    if (filter === 'Tous') return true;
    return item.statut === filter;
  });

  // Statistiques
  const stats = {
    total: encaissements.reduce((sum, item) => sum + item.montant, 0),
    encaisses: encaissements
      .filter(item => item.statut === 'Encaissé')
      .reduce((sum, item) => sum + item.montant, 0),
    enAttente: encaissements
      .filter(item => item.statut === 'En attente')
      .reduce((sum, item) => sum + item.montant, 0),
    rejetes: encaissements
      .filter(item => item.statut === 'Rejeté')
      .reduce((sum, item) => sum + item.montant, 0)
  };

  // Fonction pour le badge de statut
  const getStatusBadge = (status) => {
    switch (status) {
      case 'Encaissé':
        return <Badge color="success" pill>{status}</Badge>;
      case 'En attente':
        return <Badge color="warning" pill>{status}</Badge>;
      case 'Rejeté':
        return <Badge color="danger" pill>{status}</Badge>;
      default:
        return <Badge color="info" pill>{status}</Badge>;
    }
  };

  return (
    <Container fluid className="mt-4">
      <Row className="mb-4">
        <Col>
          <h2>Tableau de Bord - Suivi des Encaissements</h2>
          <p className="text-muted">Suivi et gestion des chèques clients</p>
        </Col>
      </Row>

      {/* Cartes de statistiques */}
      <Row className="mb-4">
        <Col md="3">
          <Card className="border-0 shadow-sm">
            <CardBody>
              <h6 className="text-uppercase text-muted mb-2">Total</h6>
              <h3 className="mb-0">{stats.total.toLocaleString()} DH</h3>
              <Progress className="mt-2" value={100} color="primary" />
            </CardBody>
          </Card>
        </Col>
        <Col md="3">
          <Card className="border-0 shadow-sm">
            <CardBody>
              <h6 className="text-uppercase text-muted mb-2">Encaissés</h6>
              <h3 className="mb-0">{stats.encaisses.toLocaleString()} DH</h3>
              <Progress className="mt-2" 
                value={(stats.encaisses / stats.total) * 100 || 0} 
                color="success" />
            </CardBody>
          </Card>
        </Col>
        <Col md="3">
          <Card className="border-0 shadow-sm">
            <CardBody>
              <h6 className="text-uppercase text-muted mb-2">En attente</h6>
              <h3 className="mb-0">{stats.enAttente.toLocaleString()} DH</h3>
              <Progress className="mt-2" 
                value={(stats.enAttente / stats.total) * 100 || 0} 
                color="warning" />
            </CardBody>
          </Card>
        </Col>
        <Col md="3">
          <Card className="border-0 shadow-sm">
            <CardBody>
              <h6 className="text-uppercase text-muted mb-2">Rejetés</h6>
              <h3 className="mb-0">{stats.rejetes.toLocaleString()} DH</h3>
              <Progress className="mt-2" 
                value={(stats.rejetes / stats.total) * 100 || 0} 
                color="danger" />
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Tableau des encaissements */}
      <Card className="shadow-sm">
        <CardHeader className="bg-white border-0 d-flex justify-content-between align-items-center">
          <div>
            <h5 className="mb-0">Liste des Encaissements</h5>
          </div>
          <div>
            <ButtonGroup>
              <Button color="light" size="sm">
                <FiRefreshCw className="mr-1" /> Actualiser
              </Button>
              <Dropdown isOpen={dropdownOpen} toggle={() => setDropdownOpen(!dropdownOpen)}>
                <DropdownToggle caret color="light" size="sm">
                  <FiFilter className="mr-1" /> {filter}
                </DropdownToggle>
                <DropdownMenu right>
                  <DropdownItem onClick={() => setFilter('Tous')}>Tous</DropdownItem>
                  <DropdownItem onClick={() => setFilter('Encaissé')}>Encaissés</DropdownItem>
                  <DropdownItem onClick={() => setFilter('En attente')}>En attente</DropdownItem>
                  <DropdownItem onClick={() => setFilter('Rejeté')}>Rejetés</DropdownItem>
                </DropdownMenu>
              </Dropdown>
              <Button color="light" size="sm">
                <FiDownload className="mr-1" /> Exporter
              </Button>
              <Button color="light" size="sm">
                <FiPrinter className="mr-1" /> Imprimer
              </Button>
            </ButtonGroup>
          </div>
        </CardHeader>
        <CardBody>
          {loading ? (
            <Alert color="info">Chargement des données...</Alert>
          ) : (
            <Table hover responsive>
              <thead>
                <tr>
                  <th>N° Chèque</th>
                  <th>Client</th>
                  <th>Montant</th>
                  <th>Date Émission</th>
                  <th>Date Échéance</th>
                  <th>Banque</th>
                  <th>Statut</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredData.map((item) => (
                  <tr key={item.id}>
                    <td>{item.numero}</td>
                    <td>{item.client}</td>
                    <td>{item.montant.toLocaleString()} DH</td>
                    <td>{new Date(item.dateEmission).toLocaleDateString()}</td>
                    <td>{new Date(item.dateEcheance).toLocaleDateString()}</td>
                    <td>{item.banque}</td>
                    <td>{getStatusBadge(item.statut)}</td>
                    <td>
                      <Button color="link" size="sm">
                        <FiEye /> Détails
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </CardBody>
      </Card>
    </Container>
  );
};

export default DashboardEncaissements;